import type {
  INeoWorkflowService,
  NeoWorkflowError,
} from "../ports/driver/neoWorkflowService";
import type {
  INeoWorkflowRepository,
  InboundEmailBindingStatus,
  SimulatedEmailPayload,
  WorkflowCreatePayload,
  WorkflowEdgeInput,
  WorkflowNodeClinicScopeCheckPayload,
  WorkflowNodeInput,
  WorkflowUpdatePayload,
} from "../ports/driven/neoWorkflowRepository";

const toError = (error: unknown): NeoWorkflowError => {
  // A stale-revision conflict (thrown by the repository on HTTP 409) is a
  // recoverable signal, not a hard failure: surface it so the caller refetches
  // and reapplies instead of showing an error. Detected via a duck-typed
  // marker to avoid importing the adapter class across the hexagon boundary.
  if (
    error &&
    typeof error === "object" &&
    (error as { graphRevisionConflict?: unknown }).graphRevisionConflict ===
      true
  ) {
    return {
      message: "The workflow graph changed since it was loaded.",
      conflict: true,
      currentRevision:
        (error as { currentRevision?: number | null }).currentRevision ?? null,
    };
  }
  return {
    message: error instanceof Error ? error.message : "Something went wrong.",
  };
};

/** The shared HTTP transport resolves (does not throw) a non-2xx response
 *  with its body, e.g. FastAPI's `{ detail }`. Returns that failure's message,
 *  or null when `resp` is not an error body. */
const errorBodyMessage = (resp: unknown): string | null => {
  if (!resp || typeof resp !== "object" || !("detail" in resp)) return null;
  const detail = (resp as { detail?: unknown }).detail;
  return typeof detail === "string" ? detail : "Something went wrong.";
};

export const NeoWorkflowServiceImplFactory = ({
  neoWorkflowRepository,
}: {
  neoWorkflowRepository: INeoWorkflowRepository;
}): INeoWorkflowService => {
  const listWorkflows: INeoWorkflowService["listWorkflows"] = async () => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowsAPI();
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listAvailableWorkflows: INeoWorkflowService["listAvailableWorkflows"] =
    async (clinicId) => {
      try {
        const resp = await neoWorkflowRepository.listAvailableWorkflowsAPI(
          clinicId
        );
        const data = (resp?.workflows ?? []).map((w) => ({
          id: String(w.workflow_id_str),
          name: w.name ?? String(w.workflow_id_str),
          description: w.description ?? null,
          category: w.category ?? null,
          extraInfo: w.extra_info ?? null,
          subscribed: Boolean(w.subscribed),
          status: w.status ?? undefined,
          lastRunAt: w.last_run_at ?? null,
          nextRunAt: w.next_run_at ?? null,
          schedule: w.schedule ?? null,
          // `paused` is the authoritative disabled state. For older backend
          // responses without status, retain the subscription fallback.
          enabled:
            w.status != null ? w.status !== "paused" : Boolean(w.subscribed),
        }));
        return { success: true, data };
      } catch (error) {
        return { success: false, error: toError(error) };
      }
    };

  const setWorkflowUserEnablement: INeoWorkflowService["setWorkflowUserEnablement"] =
    async (clinicId, workflowId, userId, enabled) => {
      try {
        const data = await neoWorkflowRepository.setWorkflowUserEnablementAPI(
          clinicId,
          workflowId,
          userId,
          enabled
        );
        const failure = errorBodyMessage(data);
        if (failure) return { success: false, error: { message: failure } };
        return { success: true, data };
      } catch (error) {
        return { success: false, error: toError(error) };
      }
    };

  const runClinicWorkflow: INeoWorkflowService["runClinicWorkflow"] = async (
    clinicId,
    workflowId
  ) => {
    try {
      const data = await neoWorkflowRepository.runClinicWorkflowAPI(
        clinicId,
        workflowId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createWorkflow = async (payload: WorkflowCreatePayload) => {
    try {
      const data = await neoWorkflowRepository.createWorkflowAPI(payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getWorkflowGraph = async (workflowId: number) => {
    try {
      const data = await neoWorkflowRepository.getWorkflowGraphAPI(workflowId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const replaceWorkflowGraph = async (
    workflowId: number,
    nodes: WorkflowNodeInput[],
    edges: WorkflowEdgeInput[],
    baseRevision?: number | null
  ) => {
    try {
      const data = await neoWorkflowRepository.replaceWorkflowGraphAPI(
        workflowId,
        nodes,
        edges,
        baseRevision
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const generateNodeOutputSchema = async (
    workflowId: number,
    nodeId: number
  ) => {
    try {
      const data = await neoWorkflowRepository.generateNodeOutputSchemaAPI(
        workflowId,
        nodeId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const restoreLastRunGraph = async (workflowId: number) => {
    try {
      const data = await neoWorkflowRepository.restoreLastRunGraphAPI(
        workflowId
      );
      if (
        !data ||
        typeof (data as { workflow?: unknown }).workflow !== "object"
      ) {
        const detail = (data as { detail?: unknown } | null)?.detail;
        return {
          success: false,
          error: {
            message:
              typeof detail === "string"
                ? detail
                : "Could not restore the last-run version.",
          },
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const keepCurrentGraphVersion = async (workflowId: number) => {
    try {
      const data = await neoWorkflowRepository.keepCurrentGraphVersionAPI(
        workflowId
      );
      if (
        !data ||
        typeof (data as { workflow?: unknown }).workflow !== "object"
      ) {
        const detail = (data as { detail?: unknown } | null)?.detail;
        return {
          success: false,
          error: {
            message:
              typeof detail === "string"
                ? detail
                : "Could not keep the current workflow version.",
          },
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const checkNodeClinicScope = async (
    workflowId: number,
    nodeId: number,
    payload?: WorkflowNodeClinicScopeCheckPayload
  ) => {
    try {
      const data = await neoWorkflowRepository.checkNodeClinicScopeAPI(
        workflowId,
        nodeId,
        payload
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const updateWorkflow = async (
    workflowId: number,
    payload: WorkflowUpdatePayload
  ) => {
    try {
      const data = await neoWorkflowRepository.updateWorkflowAPI(
        workflowId,
        payload
      );
      // The shared HTTP transport resolves a FAILED PATCH with the error
      // *body* (e.g. FastAPI's `{ detail }`) rather than throwing (see the
      // PATCH catch in apiProvider). A real workflow always carries a numeric
      // `id`; if it doesn't, the update actually failed (e.g. a 404 from the
      // owner-gate, a 400 bad cron) — report it as an error so callers do NOT
      // `setMeta` the error body and blank the workflow. Symptom this prevents:
      // a rejected schedule save wiping `schedule_type` and flipping the
      // trigger UI to "Manually".
      if (!data || typeof (data as { id?: unknown }).id !== "number") {
        const detail = (data as { detail?: unknown } | null)?.detail;
        // The update route conflates "not owned" and "not found" into a 404
        // with detail "workflow not found" (so it can't leak existence to a
        // non-owner). Since the workflow was loadable, a failed edit is an
        // ownership refusal in practice — surface a permission message rather
        // than the misleading "not found". Other details (e.g. a 400 bad-cron
        // message) are passed through unchanged.
        const message =
          detail === "workflow not found"
            ? "You do not have permission to edit this workflow."
            : typeof detail === "string"
            ? detail
            : "Could not update the workflow.";
        return { success: false, error: { message } };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteWorkflow = async (workflowId: number) => {
    try {
      await neoWorkflowRepository.deleteWorkflowAPI(workflowId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const runWorkflow = async (
    workflowId: number,
    options?: {
      inputFileIds?: number[];
      simulatedEmail?: SimulatedEmailPayload;
      clinicId?: number;
    }
  ) => {
    try {
      const data = await neoWorkflowRepository.runWorkflowAPI(workflowId, {
        input_file_ids: options?.inputFileIds,
        simulated_email: options?.simulatedEmail,
        clinic_id: options?.clinicId,
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getWorkflowRunProgress = async (runId: string) => {
    try {
      const data = await neoWorkflowRepository.getWorkflowRunProgressAPI(runId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowRuns = async (workflowId: number, limit?: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowRunsAPI(
        workflowId,
        limit
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getClinicWorkflowStatus = async (
    clinicId: number,
    workflowId: string
  ) => {
    try {
      const data = await neoWorkflowRepository.getClinicWorkflowStatusAPI(
        clinicId,
        workflowId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listClinicWorkflowRuns = async (
    clinicId: number,
    workflowId?: string
  ) => {
    try {
      const response = await neoWorkflowRepository.listClinicWorkflowRunsAPI(
        clinicId,
        workflowId
      );
      const rawRuns = Array.isArray(response)
        ? response
        : response.items ?? response.runs ?? response.data ?? [];
      const data = rawRuns.map((run) => {
        const raw = run as typeof run & Record<string, unknown>;
        const runId = String(raw.run_id ?? raw.id ?? "");
        const artifacts = (
          Array.isArray(raw.artifacts) ? raw.artifacts : []
        ).flatMap((artifact) => {
          if (!artifact || typeof artifact !== "object") return [];
          const item = artifact as Record<string, unknown>;
          const fileId = Number(item.file_id);
          if (!Number.isInteger(fileId) || fileId <= 0) return [];
          return [
            {
              ...item,
              file_id: fileId,
              filename:
                typeof item.filename === "string" ? item.filename : null,
              name:
                typeof item.name === "string"
                  ? item.name
                  : typeof item.filename === "string"
                  ? item.filename
                  : `Artifact ${fileId}`,
              content_type:
                typeof item.content_type === "string"
                  ? item.content_type
                  : null,
              mime_type:
                typeof item.mime_type === "string"
                  ? item.mime_type
                  : typeof item.content_type === "string"
                  ? item.content_type
                  : null,
              file_size:
                typeof item.file_size === "number" ? item.file_size : null,
              size_bytes:
                typeof item.size_bytes === "number"
                  ? item.size_bytes
                  : typeof item.file_size === "number"
                  ? item.file_size
                  : null,
              uploaded_at:
                typeof item.uploaded_at === "string" ? item.uploaded_at : null,
              available: item.available !== false,
            },
          ];
        });
        return {
          ...run,
          run_id: runId,
          label:
            run.label ??
            run.schedule_key ??
            run.run_mode ??
            `Run ${runId || ""}`.trim(),
          started_at: run.started_at ?? run.created_at ?? null,
          completed_at:
            run.completed_at ??
            run.sent_at ??
            run.generated_at ??
            run.created_at ??
            null,
          artifacts,
          artifact_count:
            typeof run.artifact_count === "number"
              ? run.artifact_count
              : artifacts.length,
        };
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getClinicWorkflowArtifactUrl = async (
    clinicId: number,
    runId: string,
    fileId: number
  ) => {
    try {
      const data = await neoWorkflowRepository.getClinicWorkflowArtifactUrlAPI(
        clinicId,
        runId,
        fileId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listMondayBriefingDeliveries = async (clinicId: number) => {
    try {
      const response =
        await neoWorkflowRepository.listMondayBriefingDeliveriesAPI(clinicId);
      const data = Array.isArray(response)
        ? response
        : response.items ?? response.deliveries ?? response.data ?? [];
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getMondayBriefingDeliveryEmail = async (rowId: number) => {
    try {
      const data =
        await neoWorkflowRepository.getMondayBriefingDeliveryEmailAPI(rowId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getMondayBriefingRun = async (runId: string) => {
    try {
      const data = await neoWorkflowRepository.getMondayBriefingRunAPI(runId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowRunConversations = async (runId: string) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowRunConversationsAPI(
        runId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowEligibleClinics = async (workflowId: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowEligibleClinicsAPI(
        workflowId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowEmailAddresses = async (workflowId: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowEmailAddressesAPI(
        workflowId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowEmailBindings = async (workflowId: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowEmailBindingsAPI(
        workflowId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createWorkflowEmailBinding = async (
    workflowId: number,
    addressId: number,
    clinicId: number,
    options?: {
      condition?: Record<string, unknown> | null;
      senderAllowlist?: string | null;
    }
  ) => {
    try {
      const data = await neoWorkflowRepository.createWorkflowEmailBindingAPI(
        workflowId,
        {
          address_id: addressId,
          clinic_id: clinicId,
          condition: options?.condition ?? null,
          sender_allowlist: options?.senderAllowlist ?? undefined,
        }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setWorkflowEmailBindingStatus = async (
    workflowId: number,
    bindingId: number,
    status: InboundEmailBindingStatus
  ) => {
    try {
      const data = await neoWorkflowRepository.patchWorkflowEmailBindingAPI(
        workflowId,
        bindingId,
        { status }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setWorkflowEmailBindingSenderAllowlist = async (
    workflowId: number,
    bindingId: number,
    senderAllowlist: string
  ) => {
    try {
      const data = await neoWorkflowRepository.patchWorkflowEmailBindingAPI(
        workflowId,
        bindingId,
        { sender_allowlist: senderAllowlist }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setWorkflowEmailBindingClinic = async (
    workflowId: number,
    bindingId: number,
    clinicId: number
  ) => {
    try {
      const data = await neoWorkflowRepository.patchWorkflowEmailBindingAPI(
        workflowId,
        bindingId,
        { clinic_id: clinicId }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteWorkflowEmailBinding = async (
    workflowId: number,
    bindingId: number
  ) => {
    try {
      await neoWorkflowRepository.deleteWorkflowEmailBindingAPI(
        workflowId,
        bindingId
      );
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  return {
    listWorkflows,
    listAvailableWorkflows,
    setWorkflowUserEnablement,
    runClinicWorkflow,
    createWorkflow,
    getWorkflowGraph,
    replaceWorkflowGraph,
    restoreLastRunGraph,
    keepCurrentGraphVersion,
    generateNodeOutputSchema,
    checkNodeClinicScope,
    updateWorkflow,
    deleteWorkflow,
    runWorkflow,
    getWorkflowRunProgress,
    listWorkflowRuns,
    getClinicWorkflowStatus,
    listClinicWorkflowRuns,
    getClinicWorkflowArtifactUrl,
    listMondayBriefingDeliveries,
    getMondayBriefingDeliveryEmail,
    getMondayBriefingRun,
    listWorkflowRunConversations,
    listWorkflowEligibleClinics,
    listWorkflowEmailAddresses,
    listWorkflowEmailBindings,
    createWorkflowEmailBinding,
    setWorkflowEmailBindingStatus,
    setWorkflowEmailBindingSenderAllowlist,
    setWorkflowEmailBindingClinic,
    deleteWorkflowEmailBinding,
  };
};
